/**
 * CSS Custom Properties
 *
 * These properties can be overridden using the component's tag as selector.
 *
 * @prop --calcite-combobox-input-height: Specifies the height of the component's input.
 */

:host {
  @apply relative block;
}

@include disabled();
@include xButton();

:host([scale="s"]) {
  @apply text-n2;
  --calcite-combobox-item-spacing-unit-l: theme("spacing.2");
  --calcite-combobox-item-spacing-unit-s: theme("spacing.1");
  --calcite-combobox-input-height: theme("spacing.4");
  --calcite-internal-combobox-input-margin-block: calc(theme("spacing.1") - theme("borderWidth.DEFAULT"));

  .x-button {
    margin-inline: theme("spacing.2");
  }
}

:host([scale="m"]) {
  @apply text-n1;
  --calcite-combobox-item-spacing-unit-l: theme("spacing.3");
  --calcite-combobox-item-spacing-unit-s: theme("spacing.2");
  --calcite-combobox-input-height: theme("spacing.4");
  --calcite-internal-combobox-input-margin-block: calc(theme("spacing.2") - theme("borderWidth.DEFAULT"));

  .x-button {
    margin-inline-end: theme("spacing.3");
  }
}

:host([scale="l"]) {
  @apply text-0;
  --calcite-combobox-item-spacing-unit-l: theme("spacing.4");
  --calcite-combobox-item-spacing-unit-s: theme("spacing.3");
  --calcite-combobox-input-height: theme("spacing.6");
  --calcite-internal-combobox-input-margin-block: calc(theme("spacing[2.5]") - theme("borderWidth.DEFAULT"));

  .x-button {
    margin-inline-end: theme("spacing.4");
  }
}

.wrapper {
  @apply bg-foreground-1
    text-color-1
    focus-base
    flex
    border-color-input
    border
    border-solid;
  padding-block: calc(var(--calcite-combobox-item-spacing-unit-s) / 4);
  padding-inline: var(--calcite-combobox-item-spacing-unit-l);

  &:hover {
    .icon {
      color: var(--calcite-color-text-1);
    }
  }
}

:host(:focus-within) .wrapper,
.wrapper--active {
  @apply focus-inset;
}

:host([read-only]) {
  .wrapper {
    background-color: var(--calcite-color-background);
  }

  .label {
    font-weight: var(--calcite-font-weight-medium);
  }
}

:host([status="invalid"]) .wrapper {
  @apply border-color-danger;
}

:host([status="invalid"]:focus-within) .wrapper {
  @apply focus-inset-danger;
}

.wrapper--single {
  padding-block: 0;
  padding-inline: var(--calcite-combobox-item-spacing-unit-l);
  @apply cursor-pointer flex-nowrap;
}

.grid-input {
  @apply flex
    flex-grow
    flex-wrap
    items-center
    relative
    truncate
    p-0;

  gap: var(--calcite-combobox-item-spacing-unit-s);
  margin-inline-end: var(--calcite-combobox-item-spacing-unit-s);

  &.selection-display-fit,
  &.selection-display-single {
    @apply flex-nowrap overflow-hidden;
  }
}

.input {
  @apply appearance-none
  bg-transparent
  border-none
  flex-grow
  font-inherit
  text-color-1
  text-ellipsis
  overflow-hidden
  p-0;
  font-size: inherit;
  block-size: var(--calcite-combobox-input-height);
  line-height: var(--calcite-combobox-input-height);
  inline-size: 100%;
  margin-block-end: var(--calcite-combobox-item-spacing-unit-s);
  min-inline-size: 4.8125rem;
  &:focus {
    @apply outline-none;
  }

  &:placeholder-shown {
    @apply text-ellipsis;
  }
}

.input--single {
  @apply p-0;
  margin-block: var(--calcite-internal-combobox-input-margin-block);
}

.wrapper--active .input-single {
  @apply cursor-text;
}

.input--hidden {
  @apply pointer-events-none
  w-0
  min-w-0
  opacity-0;
}

.input--icon {
  padding-block: 0;
  padding-inline: var(--calcite-combobox-item-spacing-unit-l);
}

.input-wrap {
  @apply flex flex-grow items-center;
}

.input-wrap--single {
  @apply flex-1 overflow-hidden;
}

.label {
  @apply pointer-events-none
    max-w-full
    flex-auto
    truncate
    p-0
    font-normal;
  block-size: var(--calcite-combobox-input-height);
  line-height: var(--calcite-combobox-input-height);
}

.label--icon {
  padding-inline: var(--calcite-combobox-item-spacing-unit-l);
}

.icon-end,
.icon-start {
  @apply flex cursor-pointer items-center;
}

.icon-end {
  @apply flex-none;

  .icon {
    color: var(--calcite-color-text-3);
  }
}

.floating-ui-container {
  --calcite-floating-ui-z-index: theme("zIndex.dropdown");
  @include floatingUIContainer();
  @include floatingUIWrapper();
}

@include floatingUIElemAnim(".floating-ui-container");

.floating-ui-container--active {
  @include floatingUIWrapperActive();
}

@media (forced-colors: active) {
  .wrapper,
  .floating-ui-container--active {
    border: 1px solid canvasText;
  }
}

.screen-readers-only {
  @apply sr-only;
}

.list-container {
  @apply bg-foreground-1
    max-h-menu
    overflow-y-auto;
  inline-size: var(--calcite-dropdown-width);
}

.list {
  @apply m-0 block p-0;
}

.list--hide {
  @apply h-0 overflow-hidden;
}

calcite-chip {
  --calcite-animation-timing: 0;
}

.chip {
  margin-block: calc(var(--calcite-combobox-item-spacing-unit-s) / 4);
  max-inline-size: 100%;
}

.chip--active {
  @apply bg-foreground-3;
}

.chip--invisible {
  @apply absolute invisible;
}

.item {
  @apply block;
}

@include form-validation-message();
@include hidden-form-input();
@include base-component();

::slotted(calcite-combobox-item-group:not(:first-child)) {
  padding-block-start: var(--calcite-combobox-item-spacing-unit-l);
}
